<template>
<q-page class="main">
  <common-form :action="$route('mapgroup.map.update', {mapGroup_id: mapGroup.id, kaart: map.id})" is-update>
    <q-card class="page-card">
      <q-card-section class="page-card__main-content">
        <item-header :item="map" />
        <div class="row q-col-gutter-md">
          <div class="col-xs-12 col-sm-6">
            <common-input
                v-model="form.title"
                label="Titel"
                name="title"
                :error-message="$errorMessage('title')"
                :error="$hasError('title')"
                required
            />
            <OptionGroup
                inline
                v-model="form.map_groups"
                :options="mapGroupOptions"
                :error-message="$errorMessage('map_groups')"
                :error="$hasError('map_groups')"
                name="map_groups"
                type="checkbox"
                label="Onderdeel van kaartgroepen"
            />
          </div>
          <div class="col-xs-12 col-sm-6">
            <BaseMap :is-loading="geometryLoading" :geometry="mapGeometry" auto-fit auto-fit-always height="400px" />
          </div>
        </div>
      </q-card-section>

      <q-card-actions class="q-pa-md" align="between">
        <accent-button label="Opslaan" type="submit" />
        <warning-button label="Verwijder deze kaart"
                        v-if="!rules.data.length && !structuralElements.data.length"
                        @click="deleteMap" />
        <primary-button v-else :label="replacementLabel"
                        @click="replaceMap" />

      </q-card-actions>
    </q-card>
  </common-form>
  <q-card class="page-card" v-if="rules.data.length">
    <q-card-section class="page-card__details">
      <h2 class="heading-3 q-mb-md">
        <template v-if="rules.total === 1">
          Richtlijn gekoppeld aan {{ map.title }}
        </template>
        <template v-else>
          {{ rules.total }} richtlijnen gekoppeld aan {{ map.title }}
        </template>

      </h2>
      <q-list bordered separator>
        <q-item
            tag="label"
            v-for="rule in rules.data"
            :key="rule.id"
        >
          <q-item-section avatar>
            <q-checkbox v-model="selectedRuleVersions" :val="rule.current_version.id"
                        :aria-label="`Selecteer richtlijn ${rule.code} om te verplaatsen naar andere kaart`" />
          </q-item-section>
          <q-item-section>
            <q-item-label :id="`${rule.current_version.id}-summary`" lines="2">{{
                rule.current_version.summary
              }}
            </q-item-label>
          </q-item-section>
          <q-item-section side top>
            <q-chip class="count-chip" v-if="!$isMobile">{{ rule.code }}</q-chip>
          </q-item-section>
          <q-item-section side top>
            <flat-button size="md"
                         style="padding: 0px 8px"
                         @click.stop="$goto('rule.show', {richtlijn: rule})">
              <template v-if="$isMobile">
                Naar pagina
              </template>
              <template v-else>
                Ga naar richtlijn pagina
              </template>
            </flat-button>
          </q-item-section>
        </q-item>
        <q-item v-if="rules?.last_page > 1" class="flex flex-center">
          <PaginationLinks :items="rules" pageKey="ruleVersions" />
        </q-item>
      </q-list>
    </q-card-section>
    <q-card-actions align="between">
      <div>
        <flat-button
            outline
            class="q-mr-sm"
            :disable="selectedRuleVersions.length === rules.data.length"
            @click="selectAllRuleVersions">
          <template v-if="rules?.last_page > 1">Selecteer deze {{ rules.data.length }} richtlijnen</template>
          <template v-else>Selecteer alle richtlijnen</template>
        </flat-button>
        <flat-button
            outline
            :disable="selectedRuleVersions.length === 0"
            @click="deselectAllRuleVersions">
          Deselecteer richtlijnen
        </flat-button>
      </div>
      <primary-button v-if="!structuralElements.total"
                      :label="replacementLabel"
                      @click="replaceMap" />
    </q-card-actions>
  </q-card>
  <q-card class="page-card" v-if="structuralElements.total">
    <q-card-section class="page-card__details">
      <h2 class="heading-3 q-mb-md">
        <template v-if="structuralElements.total === 1">
          Inrichtingselement gekoppeld aan {{ map.title }}
        </template>
        <template v-else>
          {{ structuralElements.total }} inrichtingselementen gekoppeld aan {{ map.title }}
        </template>

      </h2>
      <q-list bordered separator>
        <q-item
            tag="label"
            v-for="structuralElement in structuralElements.data"
            :key="structuralElement.id"
        >
          <q-item-section avatar>
            <q-checkbox v-model="selectedElements" :val="structuralElement.id"
                        :aria-label="`Selecteer inrichtingselement ${structuralElement.code} om te verplaatsen naar andere kaart`" />
          </q-item-section>
          <q-item-section>
            <q-item-label :id="`${structuralElement.id}-summary`"> {{ structuralElement.title }} (<em>{{
                structuralElement.type
              }}</em>)
            </q-item-label>
          </q-item-section>
          <q-item-section side top>
            <flat-button size="md"
                         style="padding: 0px 8px"
                         @click.stop="$goto('object.show', {object: structuralElement.id })">
              <template v-if="$isMobile">
                Naar pagina
              </template>
              <template v-else>
                Ga naar element pagina
              </template>
            </flat-button>
          </q-item-section>
        </q-item>
        <q-item v-if="structuralElements?.last_page > 1" class="flex flex-center">
          <PaginationLinks :items="structuralElements" pageKey="elements" />
        </q-item>
      </q-list>
    </q-card-section>
    <q-card-actions align="between">
      <div>
        <flat-button
            outline
            class="q-mr-sm"
            :disable="selectedElements.length === structuralElements.data.length"
            @click="selectedAllElements">
          <template v-if="structuralElements?.last_page > 1">Selecteer deze {{ structuralElements.data.length }}
            elementen
          </template>
          <template v-else>Selecteer alle elementen</template>
        </flat-button>
        <flat-button
            outline
            :disable="selectedElements.length === 0"
            @click="deselectAllElements">
          Deselecteer elementen
        </flat-button>
      </div>
      <primary-button :label="replacementLabel"
                      @click="replaceMap" />
    </q-card-actions>
  </q-card>
  <ReplaceMapDialog :map="map"
                    :map-group="mapGroup"
                    :rule-version-ids="selectedRuleVersions"
                    :structural-element-ids="selectedElements"
                    :structural-elements-enabled="!!structuralElements.total"
                    v-model="showReplaceDialog" />
</q-page>
</template>

<script>
import BaseMap from '../../components/map/BaseMap.vue'
import PrimaryButton from '../../components/button/PrimaryButton.vue'
import FlatButton from '../../components/button/FlatButton.vue'
import ReplaceMapDialog from './dialog/ReplaceMapDialog.vue'

export default {
  components: { ReplaceMapDialog, FlatButton, PrimaryButton, BaseMap },
  props: {
    map: {
      type: Object,
      default: () => ({}),
    },
    mapGroup: {
      type: Object,
      default: () => ({}),
    },
    mapGroups: {
      type: Array,
      default: () => ([]),
    },
    rules: {
      type: Array,
      default: () => ([]),
    },
    structuralElements: {
      type: Object,
      default: () => ([]),
    },
  },
  data () {
    return {
      form: {
        title: this.$old('title', this.map.title),
        map_groups: this.$old('map_groups', this.map.map_groups.map(({ id }) => id)),
      },
      mapGroupOptions: this.mapGroups.map((mapGroup) => ({
        label: mapGroup.title,
        value: mapGroup.id,
      })),
      selectedRuleVersions: [],
      selectedElements: [],
      showReplaceDialog: false,
      geometryLoading: true,
      mapGeometry: '',
    }
  },
  computed: {
    replacementLabel () {
      if (!this.structuralElements.total) {
        if (this.selectedRuleVersions.length) {
          return `Vervang deze kaart bij de ${this.selectedRuleVersions.length} geselecteerde richtlijnen`
        }
        return `Vervang deze kaart bij alle ${this.rules.total} richtlijnen`
      }
      if (this.selectedRuleVersions.length && this.selectedElements.length) {
        return `Vervang deze kaart bij ${this.selectedElements.length} elementen en ${this.selectedRuleVersions.length} richtlijnen`
      }
      if (this.selectedRuleVersions.length) {
        return `Vervang deze kaart bij de ${this.selectedRuleVersions.length} geselecteerde richtlijnen`
      }
      if (this.selectedElements.length) {
        return `Vervang deze kaart bij ${this.selectedElements.length} elementen`
      }
      return `Vervang deze kaart bij alle ${this.structuralElements.total} elementen en ${this.rules.total} richtlijnen`
    },
  },
  methods: {
    async deleteMap () {
      if (!await this.$confirm({ title: 'Weet u zeker dat u deze kaart wilt verwijderen?' })) {
        return
      }
      try {
        await axios.delete(this.$route('mapgroup.map.destroy', { mapGroup_id: this.mapGroup.id, map_id: this.map.id }))
        await this.$toast('success', 'De kaart is verwijderd')
        await new Promise(resolve => setTimeout(resolve, 500))
        this.$goto('mapgroup.edit', { kaartgroep: this.mapGroup.id })
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De kaart kon niet worden verwijderd')
      }
    },
    selectAllRuleVersions () {
      this.selectedRuleVersions = this.rules.data.map(({ current_version }) => current_version.id)
    },
    deselectAllRuleVersions () {
      this.selectedRuleVersions = []
    },
    selectedAllElements () {
      this.selectedElements = this.structuralElements.data.map(({ id }) => id)
    },
    deselectAllElements () {
      this.selectedElements = []
    },
    async replaceMap () {
      this.showReplaceDialog = true
    },
  },
  mounted () {
    axios.get(
        this.$route('api.map.show', { map: this.map.id }),
    ).then(({ data }) => {
      this.mapGeometry = data
    }).catch((e) => {
      console.log(e)
    }).finally(() => {
      this.geometryLoading = false
    })
  },
}
</script>
